import { Checkbox, CheckboxGroup, TextOrHTML } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import type React from "react";
import { isShadowQuestion } from "../../../../../../../features/questionnaire";
import { containsHtmlMarkup, snakeCase } from "../../../../../../../utils";
import type { QuestionComponentProps } from "../../types";

const makeLabel = (label: string) => {
  if (containsHtmlMarkup(label)) {
    return <TextOrHTML text={label} />;
  }
  return label;
};

export const triggerShadowQuestionAnswerEvent = ({
  previousValue,
  newValues,
  snowplow,
  sectionTitle,
}: {
  previousValue: string[];
  newValues: string[];
  snowplow: ReturnType<typeof useSnowplowContext>["snowplow"];
  sectionTitle: string | undefined;
}) => {
  const previousValues = new Set(previousValue);
  const newValuesSet = new Set(newValues);

  const added = newValues.filter(v => !previousValues.has(v));
  const removed = previousValue.filter(v => !newValuesSet.has(v));

  const changedItem = added[0] || removed[0];

  snowplow?.trigger("questionAnswered", {
    section: sectionTitle ?? "",
    question: snakeCase(changedItem),
    answer: added[0] ? "yes" : "no",
  });
};

export const MultiSelectQuestion = ({
  options,
  config,
  sectionTitle,
  questionFieldProps,
}: QuestionComponentProps) => {
  const { value, onChange, questionLabelProps, helpInfo, ...otherProps } =
    questionFieldProps;
  const lastItemDisables = !!config?.lastItemDisables;

  const { snowplow } = useSnowplowContext();

  if (!options?.length) {
    console.warn(`No required options found`);
    return null;
  }

  const handleGroupChange = (values: string[], shouldUpdate?: boolean) => {
    if (isShadowQuestion(questionFieldProps.name)) {
      triggerShadowQuestionAnswerEvent({
        previousValue: value as string[],
        newValues: values,
        snowplow,
        sectionTitle,
      });
    }

    onChange!({
      target: { name: questionFieldProps.name, value: values, shouldUpdate },
    });
  };

  const handleOptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    optionValue: string,
  ) => {
    const isChecked = event.target.checked;

    // If lastItemDisables=true and the last option is checked,
    // we want to uncheck all other options and only keep the last option checked
    const isLastOption = optionValue === options[options.length - 1].value;
    if (lastItemDisables && isChecked && isLastOption) {
      handleGroupChange([optionValue], true);
      return;
    }

    const currentValues = new Set(value as string[]);
    if (isChecked) {
      currentValues.add(optionValue);
    } else {
      currentValues.delete(optionValue);
    }

    handleGroupChange(Array.from(currentValues), true);
  };

  return (
    <CheckboxGroup
      {...otherProps}
      lastItemDisables={lastItemDisables}
      defaultValue={value as string[]}
      onChange={handleGroupChange}
    >
      {options.map(option => (
        <Checkbox
          key={option.value}
          label={makeLabel(option.label)}
          value={option.value}
          onChange={
            option.shouldUpdate
              ? e => handleOptionChange(e, option.value)
              : undefined
          }
        />
      ))}
    </CheckboxGroup>
  );
};
